import { useState, useMemo, useEffect, useRef } from 'react';

// Helper function to safely parse JSON data
const safeJsonParse = (data: any, defaultValue: any) => {
  if (!data) return defaultValue;
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch {
    return defaultValue;
  }
};

export const useProductColors = (product: any) => {
  // Get available colors
  const availableColors = useMemo(() => {
    if (!product) return [];
    const colors = safeJsonParse(product.available_colors, []);
    const enabledColors = colors.filter((color: any) => color.enabled);
    return enabledColors;
  }, [product?.available_colors]);

  const availableSizes = useMemo(() => {
    if (!product) return [];
    const sizes = safeJsonParse(product.available_sizes, []);
    return sizes.filter((size: any) => size.enabled);
  }, [product?.available_sizes]);

  // Initialize selected color with proper default
  const [selectedColor, setSelectedColor] = useState<any>(null);
  const [isCarouselNavigating, setIsCarouselNavigating] = useState(false);
  const preventColorReset = useRef(false);

  // Update selected color when product or colors change
  useEffect(() => {
    if (product && availableColors.length > 0 && !selectedColor && !preventColorReset.current) {
      let defaultColor = null;
      
      // Try to use the product's default color first
      if (product.default_color) {
        defaultColor = availableColors.find((c: any) => c.id === product.default_color);
      }
      
      // If no default color is set or default color not found in available colors, use first available
      if (!defaultColor && availableColors.length > 0) {
        defaultColor = availableColors[0];
      }
      
      if (defaultColor) {
        setSelectedColor(defaultColor);
      }
    }
  }, [product, availableColors, selectedColor]);

  // Clear selected color when product changes (to allow re-initialization)
  useEffect(() => {
    if (product) {
      preventColorReset.current = false;
      setSelectedColor(null);
    }
  }, [product?.id]);

  const handleColorChange = (color: any) => {
    preventColorReset.current = false;
    setSelectedColor(color);
    setIsCarouselNavigating(false);
  };

  // Updated to NOT clear color selection when navigating carousel
  const handleCarouselNavigation = () => {
    // Do nothing - keep the selected color when navigating carousel
    setIsCarouselNavigating(true);
  };

  return {
    availableColors,
    availableSizes,
    selectedColor,
    isCarouselNavigating,
    handleColorChange,
    handleCarouselNavigation
  };
};
